import { type Editor } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import { type MutableRefObject } from 'react';

import {
  clearSyntheticSlashTrigger,
  getEditorViewSafe,
  getSyntheticSlashTriggerRange,
  hasPendingSlashTrigger,
  hasSyntheticSlashTrigger,
  isEditorViewReady,
  isSlashMenuVisible,
  markSyntheticSlashTrigger,
  replaySlashTokenAtCursor,
  slashCommandsPluginKey,
} from './slashCore';
import type { FocusPlacement, SuperTextPendingFocus } from './slashGlobals';

const focusInputMaxAgeMs = 4000;

type SlashFocusRefs = {
  editorMountedAtRef: MutableRefObject<number>;
  lastSlashFocusAtRef: MutableRefObject<number>;
  inFlightPendingFocusTsRef: MutableRefObject<number | null>;
  inFlightPendingFocusStartedAtRef: MutableRefObject<number>;
};

type CreateSlashFocusRuntimeParams = {
  editor: Editor;
  fieldName: string;
  enableSlashCommands: boolean;
  debugSlash: boolean;
  replayEnabled: boolean;
  retryEnabled: boolean;
  refs: SlashFocusRefs;
};

type FocusHandler = (focused: boolean) => void;

const setSelectionWithSlash = (editor: Editor, from: number, to: number) => {
  const tr = editor.state.tr.insertText('/', from, to);
  tr.setSelection(TextSelection.create(tr.doc, Math.min(from + 1, tr.doc.content.size)));
  const view = getEditorViewSafe(editor);
  if (!view) return;
  view.dispatch(tr);
};

const retriggerExistingSlashAtCursor = (editor: Editor) => {
  const view = getEditorViewSafe(editor);
  if (!view) return;
  const { from } = view.state.selection;
  const slashFrom = Math.max(from - 1, 1);
  const current = view.state.doc.textBetween(slashFrom, from, '\n', '\n');
  if (current !== '/') return;

  const deleteTr = view.state.tr.delete(slashFrom, from);
  view.dispatch(deleteTr);

  const nextFrom = Math.min(slashFrom, view.state.doc.content.size);
  const insertTr = view.state.tr.insertText('/', nextFrom, nextFrom);
  insertTr.setSelection(TextSelection.create(insertTr.doc, Math.min(nextFrom + 1, insertTr.doc.content.size)));
  view.dispatch(insertTr);
};

const focusEditorAtPlacement = (editor: Editor, placement?: FocusPlacement) => {
  if (!isEditorViewReady(editor)) return { focused: false, element: null as HTMLElement | null };
  const view = getEditorViewSafe(editor);
  if (!view) return { focused: false, element: null as HTMLElement | null };

  view.focus();
  try {
    editor.commands.focus(placement ?? 'end');
  } catch {
    return { focused: false, element: null as HTMLElement | null };
  }

  const pmElement = view.dom as HTMLElement | null;
  if (pmElement && document.activeElement !== pmElement) {
    pmElement.focus();
  }

  const focused = editor.isFocused || (pmElement ? document.activeElement === pmElement : false);
  return { focused, element: pmElement };
};

export const createSlashFocusRuntime = ({
  editor,
  fieldName,
  enableSlashCommands,
  debugSlash,
  replayEnabled,
  retryEnabled,
  refs,
}: CreateSlashFocusRuntimeParams) => {
  const { editorMountedAtRef, lastSlashFocusAtRef, inFlightPendingFocusTsRef, inFlightPendingFocusStartedAtRef } = refs;
  let syntheticCleanupTimeoutId: number | null = null;
  let cleanupPendingSlashTimeoutId: number | null = null;
  const openSlashRetryTimeouts = new Set<number>();
  const clearOpenSlashRetries = () => {
    openSlashRetryTimeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
    openSlashRetryTimeouts.clear();
  };
  const clearSyntheticCleanupTimeout = () => {
    if (!syntheticCleanupTimeoutId) return;
    window.clearTimeout(syntheticCleanupTimeoutId);
    syntheticCleanupTimeoutId = null;
  };
  const clearPendingSlashCleanupTimeout = () => {
    if (!cleanupPendingSlashTimeoutId) return;
    window.clearTimeout(cleanupPendingSlashTimeoutId);
    cleanupPendingSlashTimeoutId = null;
  };

  const ensureSlashSuggestionOpen = () => {
    const slashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
    if (slashState?.active) return;
    if (!hasPendingSlashTrigger(editor)) return;
    if (debugSlash) {
      console.debug('[slash-ab][supertext] runSlashInsertion:replay', { fieldName });
    }
    const replayed = replaySlashTokenAtCursor(editor);
    if (!replayed) return;
    window.setTimeout(() => {
      if (!isEditorViewReady(editor)) return;
      const latestSlashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
      if (latestSlashState?.active) return;
      if (!hasPendingSlashTrigger(editor)) return;
      replaySlashTokenAtCursor(editor);
    }, 0);
  };

  const cleanupPendingSlashWithoutMenu = () => {
    if (!isEditorViewReady(editor)) return;
    const slashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
    if (slashState?.active || isSlashMenuVisible()) return;
    if (!hasPendingSlashTrigger(editor)) return;
    const view = getEditorViewSafe(editor);
    if (!view) return;
    const { selection } = view.state;
    if (!selection.empty) return;
    const { from, $from } = selection;
    const textBefore = view.state.doc.textBetween($from.start(), from, '\n', '\n');
    const trailingSlashQuery = /\/\S*$/.exec(textBefore)?.[0];
    if (!trailingSlashQuery) return;
    const deleteFrom = Math.max($from.start(), from - trailingSlashQuery.length);
    if (deleteFrom >= from) return;
    view.dispatch(view.state.tr.delete(deleteFrom, from));
  };

  const cleanupStaleSyntheticSlash = () => {
    if (!hasSyntheticSlashTrigger(editor)) return;
    const slashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
    if (slashState?.active || isSlashMenuVisible()) return;
    const view = getEditorViewSafe(editor);
    if (!view) return;
    const syntheticRange = getSyntheticSlashTriggerRange(editor);
    if (!syntheticRange) {
      clearSyntheticSlashTrigger(editor);
      return;
    }
    const docSize = view.state.doc.content.size;
    const from = Math.max(1, Math.min(syntheticRange.from, docSize));
    const to = Math.max(from + 1, Math.min(syntheticRange.to, docSize));
    const current = view.state.doc.textBetween(from, to, '\n', '\n');
    if (current === '/') {
      view.dispatch(view.state.tr.delete(from, to));
    }
    clearSyntheticSlashTrigger(editor);
  };

  const scheduleSyntheticCleanup = () => {
    clearSyntheticCleanupTimeout();
    const runCleanup = (attempt = 0) => {
      cleanupStaleSyntheticSlash();
      if (!hasSyntheticSlashTrigger(editor)) {
        syntheticCleanupTimeoutId = null;
        return;
      }
      if (attempt >= 10) {
        clearSyntheticSlashTrigger(editor);
        syntheticCleanupTimeoutId = null;
        return;
      }
      syntheticCleanupTimeoutId = window.setTimeout(() => runCleanup(attempt + 1), 350);
    };
    syntheticCleanupTimeoutId = window.setTimeout(() => runCleanup(0), 900);
  };

  const runSlashInsertion = (synthetic = false) => {
    if (!enableSlashCommands) {
      if (debugSlash) {
        console.debug('[slash-ab][supertext] runSlashInsertion:skip-disabled', { fieldName });
      }
      return;
    }

    const slashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
    if (slashState?.active) return;
    const view = getEditorViewSafe(editor);
    if (!view) return;
    const { from, to } = view.state.selection;
    const slashFrom = Math.max(from - 1, 1);
    const charBeforeCursor = view.state.doc.textBetween(slashFrom, from, '\n', '\n');
    if (debugSlash) {
      console.debug('[slash-ab][supertext] runSlashInsertion:start', {
        fieldName,
        from,
        to,
        charBeforeCursor,
        isFocused: editor.isFocused,
      });
    }
    if (charBeforeCursor === '/') {
      if (synthetic) {
        markSyntheticSlashTrigger(editor, { from: slashFrom, to: from });
        scheduleSyntheticCleanup();
      }
      retriggerExistingSlashAtCursor(editor);
      requestAnimationFrame(() => {
        if (!isEditorViewReady(editor)) return;
        ensureSlashSuggestionOpen();
      });
      return;
    }
    if (synthetic) {
      markSyntheticSlashTrigger(editor, { from, to: from + 1 });
      scheduleSyntheticCleanup();
    }
    setSelectionWithSlash(editor, from, to);
    requestAnimationFrame(() => {
      if (!isEditorViewReady(editor)) return;
      ensureSlashSuggestionOpen();
    });
  };

  const triggerSlashInsertion = (openSlash: boolean) => {
    if (!openSlash) return;
    const tryInsert = (attempt = 0) => {
      if (!isEditorViewReady(editor)) {
        if (attempt < 6) {
          window.setTimeout(() => tryInsert(attempt + 1), 40);
        }
        return;
      }
      runSlashInsertion(openSlash);
    };
    tryInsert(0);

    const replayWhenSettled = (delay: number) => {
      window.setTimeout(() => {
        if (!isEditorViewReady(editor)) return;
        const slashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
        if (slashState?.active && isSlashMenuVisible()) return;
        if (!hasPendingSlashTrigger(editor) && !slashState?.active) return;
        replaySlashTokenAtCursor(editor);
      }, delay);
    };

    replayWhenSettled(320);
    replayWhenSettled(700);
  };

  const focusForSlash = (placement?: FocusPlacement, onFocused?: FocusHandler, openSlash = false) => {
    if (!isEditorViewReady(editor)) {
      onFocused?.(false);
      return false;
    }
    if (debugSlash) {
      console.debug('[slash-ab][supertext] focusForSlash', {
        fieldName,
        placement: placement ?? 'end',
        openSlash,
        replayEnabled,
        retryEnabled,
      });
    }

    const immediate = focusEditorAtPlacement(editor, placement);
    if (immediate.focused) {
      triggerSlashInsertion(openSlash);
      onFocused?.(true);
      return true;
    }

    requestAnimationFrame(() => {
      if (!isEditorViewReady(editor)) {
        onFocused?.(false);
        return false;
      }
      const now = Date.now();
      if (now - lastSlashFocusAtRef.current < 40) {
        onFocused?.(false);
        return false;
      }
      lastSlashFocusAtRef.current = now;

      const firstPass = focusEditorAtPlacement(editor, placement);
      if (!firstPass.element) {
        onFocused?.(false);
        return false;
      }

      requestAnimationFrame(() => {
        let focused = editor.isFocused || (firstPass.element ? document.activeElement === firstPass.element : false);
        if (!focused && firstPass.element) {
          firstPass.element.focus();
          focused = editor.isFocused || document.activeElement === firstPass.element;
        }
        onFocused?.(focused);
        if (!focused) {
          window.setTimeout(() => {
            if (!isEditorViewReady(editor)) return;
            const retryPass = focusEditorAtPlacement(editor, placement);
            const retryFocused = retryPass.focused;
            onFocused?.(retryFocused);
            if (!retryFocused) return false;
            triggerSlashInsertion(openSlash);
            return true;
          }, 40);
          return false;
        }

        triggerSlashInsertion(openSlash);
        return true;
      });
      return true;
    });

    return false;
  };

  const consumePendingFocus = () => {
    const map = window.__superTextFocusInputMap;
    const handledMap = (window.__superTextFocusHandled ||= {});
    if (!map || !fieldName || !map[fieldName]) return false;
    const pending = map[fieldName] as SuperTextPendingFocus;
    const pendingTs = pending.ts ?? 0;
    if (pending.ts && Date.now() - pending.ts > focusInputMaxAgeMs) {
      delete map[fieldName];
      inFlightPendingFocusTsRef.current = null;
      return false;
    }
    const clearPending = () => {
      delete map[fieldName];
      inFlightPendingFocusTsRef.current = null;
      inFlightPendingFocusStartedAtRef.current = 0;
      pending.consuming = false;
    };
    if (editor.isDestroyed || !isEditorViewReady(editor)) {
      return false;
    }
    if (pending.openSlash && editorMountedAtRef.current > 0 && Date.now() - editorMountedAtRef.current < 260) {
      return true;
    }
    const currentSlashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
    if (currentSlashState?.active) {
      if (pending.openSlash) {
        if (isSlashMenuVisible()) {
          clearPending();
          handledMap[fieldName] = Math.max(Date.now(), pending.ts ?? 0);
          return false;
        }
      } else {
        clearPending();
        return false;
      }
    }
    if (inFlightPendingFocusTsRef.current === pendingTs) {
      if (Date.now() - inFlightPendingFocusStartedAtRef.current > 500) {
        inFlightPendingFocusTsRef.current = null;
        inFlightPendingFocusStartedAtRef.current = 0;
        pending.consuming = false;
      } else {
        return true;
      }
    }
    if (pending.consuming && Date.now() - pendingTs > 500) {
      pending.consuming = false;
    }
    if (pending.consuming && inFlightPendingFocusTsRef.current !== pendingTs) {
      return true;
    }
    inFlightPendingFocusTsRef.current = pendingTs;
    inFlightPendingFocusStartedAtRef.current = Date.now();
    pending.consuming = true;
    const clearInFlight = () => {
      inFlightPendingFocusTsRef.current = null;
      inFlightPendingFocusStartedAtRef.current = 0;
    };
    let didSucceed = false;
    const watchdogId = window.setTimeout(() => {
      if (inFlightPendingFocusTsRef.current === pendingTs) {
        clearInFlight();
        if (!didSucceed) {
          pending.consuming = false;
          delete map[fieldName];
        }
      }
    }, 600);
    focusForSlash(
      pending.placement as FocusPlacement,
      focused => {
        if (!focused || didSucceed) return;
        didSucceed = true;
        window.clearTimeout(watchdogId);
        window.setTimeout(() => {
          const stabilized = pending.openSlash ? isSlashMenuVisible() : true;
          if (stabilized) {
            clearInFlight();
            delete map[fieldName];
            pending.consuming = false;
            handledMap[fieldName] = Math.max(Date.now(), pending.ts ?? 0);
            return;
          }
          clearInFlight();
          pending.consuming = false;
          delete map[fieldName];
        }, 220);
      },
      Boolean(pending.openSlash),
    );
    if (debugSlash) {
      console.debug('[slash-ab][supertext] consumePendingFocus', {
        fieldName,
        hadPending: true,
        didDelete: false,
        replayEnabled,
        retryEnabled,
      });
    }
    return true;
  };

  const handleFocusInput = (event: Event) => {
    const handledMap = (window.__superTextFocusHandled ||= {});
    const map = window.__superTextFocusInputMap;
    const openSlashRequestedAtMap = (window.__superTextOpenSlashRequestedAt ||= {});
    const detail = (
      event as CustomEvent<{
        name?: string;
        placement?: FocusPlacement;
        open_slash?: boolean;
        openSlash?: boolean;
        __replay?: boolean;
      }>
    ).detail;
    if (!detail?.name || detail.name !== fieldName) return;
    const requestedOpenSlash = detail.open_slash === true || detail.openSlash === true;
    const scheduleOpenSlashRetry = (delay: number) => {
      const timeoutId = window.setTimeout(() => {
        openSlashRetryTimeouts.delete(timeoutId);
        if (!requestedOpenSlash) return;
        if (!isEditorViewReady(editor)) return;
        const slashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
        if (slashState?.active && isSlashMenuVisible()) return;
        if (!hasPendingSlashTrigger(editor) && !slashState?.active) return;
        focusForSlash(detail.placement, undefined, true);
      }, delay);
      openSlashRetryTimeouts.add(timeoutId);
    };

    if (requestedOpenSlash) {
      clearOpenSlashRetries();
      scheduleOpenSlashRetry(120);
      scheduleOpenSlashRetry(260);
      scheduleOpenSlashRetry(420);
    }

    if (requestedOpenSlash) {
      const requestedAt = Date.now();
      openSlashRequestedAtMap[fieldName] = requestedAt;
      clearPendingSlashCleanupTimeout();
      cleanupPendingSlashTimeoutId = window.setTimeout(() => {
        cleanupPendingSlashWithoutMenu();
        cleanupPendingSlashTimeoutId = null;
      }, 1800);
      window.setTimeout(() => {
        if ((openSlashRequestedAtMap[fieldName] ?? 0) === requestedAt) {
          delete openSlashRequestedAtMap[fieldName];
        }
      }, 2600);
    }
    if (editor.isDestroyed || !isEditorViewReady(editor)) return;
    if (detail.__replay) {
      const slashState = slashCommandsPluginKey.getState(editor.state) as { active?: boolean } | null;
      if (slashState?.active && isSlashMenuVisible()) return;
    }

    if (map?.[fieldName]) {
      consumePendingFocus();
      return;
    }

    focusForSlash(
      detail.placement,
      focused => {
        if (focused) {
          handledMap[fieldName] = Date.now();
        }
      },
      requestedOpenSlash,
    );
  };

  const startPendingFocusRetryLoop = () => {
    if (!retryEnabled) return () => undefined;
    const intervalId = window.setInterval(() => {
      const map = window.__superTextFocusInputMap;
      if (!map?.[fieldName]) {
        window.clearInterval(intervalId);
        return;
      }
      consumePendingFocus();
    }, 40);
    const timeoutId = window.setTimeout(() => window.clearInterval(intervalId), 2000);
    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  };

  const destroy = () => {
    clearOpenSlashRetries();
    clearSyntheticCleanupTimeout();
    clearPendingSlashCleanupTimeout();
    const openSlashRequestedAtMap = window.__superTextOpenSlashRequestedAt;
    if (openSlashRequestedAtMap) {
      delete openSlashRequestedAtMap[fieldName];
    }
  };

  return {
    consumePendingFocus,
    handleFocusInput,
    startPendingFocusRetryLoop,
    destroy,
  };
};
